import { MoreVerticalIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { AvatarIniciales } from '@/components/marca/Avatar'
import { Boton } from '@/components/ui/Boton'
import { Campo, Entrada } from '@/components/ui/Campo'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
} from '@/components/ui/MenuDesplegable'
import { useAvisar } from '@/components/ui/avisador-contexto'
import {
  integranteEntrada,
  type IntegranteEntrada,
  type IntegranteEquipo,
} from '@/lib/schemas/admin'
import { useAgregarIntegrante, useEquipo, useQuitarIntegrante } from './api'
import { Confirmacion } from './Confirmacion'
import { useFormulario } from './formulario'
import { ModalMarco } from './ModalMarco'

interface ModalEquipoProps {
  abierto: boolean
  alCerrar: () => void
}

/**
 * C8 · Equipo RASTRO (§8.7, diferencia 8): quién ve todos los clientes y
 * proyectos. Cambios al instante; no se puede quitar a la última integrante.
 */
export function ModalEquipo({ abierto, alCerrar }: ModalEquipoProps) {
  const equipo = useEquipo(abierto)
  const agregar = useAgregarIntegrante()
  const quitar = useQuitarIntegrante()
  const avisar = useAvisar()
  const [nuevo, setNuevo] = useState<IntegranteEntrada>({ nombre: '', email: '' })
  const [aQuitar, setAQuitar] = useState<IntegranteEquipo | null>(null)
  const form = useFormulario(
    integranteEntrada,
    async (datos) => {
      await agregar.mutateAsync(datos)
      avisar(`${datos.nombre} ya es parte del equipo`)
      setNuevo({ nombre: '', email: '' })
    },
    () => undefined,
  )

  const integrantes = equipo.data ?? []

  return (
    <>
      <ModalMarco
        abierto={abierto}
        alCerrar={alCerrar}
        titulo="Equipo RASTRO"
        descripcion="Las cuentas @somosrastro.com.ar ya son del equipo, sin cargarlas. Acá se agregan otras cuentas que también ven todos los clientes y proyectos."
        error={form.errorGeneral}
        alCambiar={form.limpiar}
        ocupado={form.ocupado}
        alEnviar={() => {
          form.validarYEnviar(nuevo)
        }}
      >
        <ul className="divide-y divide-borde" aria-label="Integrantes">
          {equipo.isPending && (
            <li className="py-3 text-sm text-tinta-suave">Cargando…</li>
          )}
          {integrantes.map((i) => (
            <li key={i.id} className="flex items-center gap-3 py-2.5">
              <AvatarIniciales nombre={i.nombre} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{i.nombre}</p>
                <p className="truncate text-xs text-tinta-suave">{i.email}</p>
              </div>
              <MenuDesplegable>
                <MenuDisparador asChild>
                  <Boton
                    variante="sutil"
                    tamano="icono"
                    aria-label={`Opciones de ${i.nombre}`}
                  >
                    <MoreVerticalIcon className="size-5" aria-hidden />
                  </Boton>
                </MenuDisparador>
                <MenuContenido>
                  <MenuItem
                    peligro
                    disabled={integrantes.length <= 1}
                    {...(integrantes.length <= 1
                      ? { detalle: 'Es la última integrante' }
                      : {})}
                    onSelect={() => {
                      setAQuitar(i)
                    }}
                  >
                    <Trash2Icon className="size-4" aria-hidden />
                    Quitar acceso
                  </MenuItem>
                </MenuContenido>
              </MenuDesplegable>
            </li>
          ))}
        </ul>

        <fieldset className="border-t border-borde pt-4">
          <legend className="sr-only">Agregar persona</legend>
          <p className="mb-3 text-sm font-medium">Agregar persona</p>
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <Campo etiqueta="Nombre" error={form.errores.nombre}>
              {(p) => (
                <Entrada
                  id={p.id}
                  aria-invalid={p.invalido}
                  value={nuevo.nombre}
                  onChange={(e) => {
                    setNuevo((a) => ({ ...a, nombre: e.target.value }))
                  }}
                  placeholder="Nombre"
                  maxLength={120}
                />
              )}
            </Campo>
            <Campo etiqueta="Mail" error={form.errores.email}>
              {(p) => (
                <Entrada
                  id={p.id}
                  type="email"
                  aria-invalid={p.invalido}
                  value={nuevo.email}
                  onChange={(e) => {
                    setNuevo((a) => ({ ...a, email: e.target.value }))
                  }}
                  placeholder="mail@somosrastro.com.ar"
                  autoCapitalize="none"
                  inputMode="email"
                />
              )}
            </Campo>
            <Boton type="submit" disabled={form.ocupado} className="sm:mb-0">
              <PlusIcon className="size-4" aria-hidden />
              Agregar
            </Boton>
          </div>
          <p className="mt-3 text-xs text-tinta-suave">
            Para cargar y ver el contenido de un cliente, tienen que tener acceso a su
            carpeta de Drive.
          </p>
        </fieldset>
      </ModalMarco>

      <Confirmacion
        abierto={aQuitar !== null}
        alCerrar={() => {
          setAQuitar(null)
        }}
        titulo="Quitar del equipo"
        texto={`${aQuitar?.nombre ?? ''} va a dejar de ver todos los clientes y proyectos de inmediato.`}
        confirmar="Quitar acceso"
        peligro
        alConfirmar={async () => {
          if (!aQuitar) return
          await quitar.mutateAsync(aQuitar.id)
          avisar('Acceso quitado')
        }}
      />
    </>
  )
}
